import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Typography, Box, Card } from "@mui/material";

// Professional distinct color palette
const COLORS = [
  "#2c3e50", // Dark Navy
  "#e74c3c", // Red
  "#3498db", // Blue
  "#f39c12", // Orange
  "#27ae60", // Green
  "#9b59b6", // Purple
  "#34495e", // Dark Gray
  "#1abc9c", // Teal
  "#e67e22", // Dark Orange
  "#95a5a6", // Silver
  "#16a085", // Dark Teal
  "#d35400", // Burnt Orange
];

const StackedBarChart = ({ title, data, topPrompts }) => {
  if (!data || data.length === 0) {
    return (
      <Card
        sx={{
          flex: 4,
          p: 3,
          boxShadow: 3,
          borderRadius: 4,
          color: "text.secondary",
          mb: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Typography>No data available</Typography>
      </Card>
    );
  }

  // Create a map of promptId to color
  const promptColorMap = {};
  topPrompts.forEach((prompt, index) => {
    promptColorMap[prompt.promptId] = COLORS[index % COLORS.length];
  });

  // Transform data for stacked bar chart
  // Convert promptUsageByDate to the format recharts expects
  const chartData = data.map(item => {
    const dataPoint = { date: item.date };
    
    // Add each prompt's count as a separate field
    item.prompts.forEach(prompt => {
      dataPoint[prompt.promptId] = prompt.count;
    });
    
    return dataPoint;
  }).sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort chronologically (oldest first)

  // Custom tooltip to show breakdown
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      
      return (
        <Box
          sx={{
            backgroundColor: "white",
            p: 2,
            border: "1px solid #ccc",
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Total: {total}
          </Typography>
          {payload
            .sort((a, b) => b.value - a.value)
            .map((entry, index) => (
              <Typography
                key={`item-${index}`}
                variant="body2"
                sx={{ color: entry.color, fontSize: "0.85rem" }}
              >
                {entry.name}: {entry.value}
              </Typography>
            ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card
      sx={{
        flex: 4,
        p: 3,
        boxShadow: 3,
        borderRadius: 4,
        color: "text.secondary",
        mb: 4,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 3,
          fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <ResponsiveContainer width="100%" height={450}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
            barSize={40}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e1e1" />
            <XAxis 
              dataKey="date" 
              stroke="#666"
              angle={-45}
              textAnchor="end"
              height={80}
              style={{ fontSize: '0.75rem' }}
            />
            <YAxis 
              stroke="#666"
              style={{ fontSize: '0.875rem' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ 
                paddingTop: "20px",
                fontSize: '0.875rem',
              }}
              iconType="circle"
            />
            {topPrompts.map((prompt, index) => (
              <Bar
                key={prompt.promptId}
                dataKey={prompt.promptId}
                stackId="a"
                fill={promptColorMap[prompt.promptId]}
                name={prompt.promptName}
                radius={index === topPrompts.length - 1 ? [8, 8, 0, 0] : 0}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
};

export default StackedBarChart;

